import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import usePersistedState from '@/lib/usePersistedState';
import { ko as koLocale } from 'date-fns/locale';
import { useI18n } from '@/lib/i18n';
import { useSettings } from '@/lib/settings';
import { useWorkspace } from '@/lib/workspace';
import { playCompletionAnimation, playCompletionSound } from '@/lib/completion';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Plus, Trash, Repeat, Flag, ClipboardText, CheckSquare, ArrowCounterClockwise, CalendarBlank, Funnel, ShareNetwork, Check } from '@phosphor-icons/react';
import { format, parseISO, addDays, addWeeks, addMonths, addYears, subDays, startOfDay, endOfWeek, endOfMonth, isBefore, isSameDay, isAfter } from 'date-fns';
import { toast } from 'sonner';
import api from '@/lib/api';
import QuickAdd from '@/components/QuickAdd';
import TaskEditor from '@/components/TaskEditor';
import ShareListDialog from '@/components/ShareListDialog';

const PRIORITY_COLORS = ['', 'text-green-500', 'text-yellow-500', 'text-red-500'];

function BulkActionBar({ count, onSetDate, onShift, onRemoveDate, onCancel }) {
  const { t } = useI18n();
  const [dateValue, setDateValue] = useState('');
  return (
    <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-16 lg:left-56 bg-card border-t border-border shadow-lg z-40">
      <div className="max-w-4xl mx-auto px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground shrink-0">{t('bulk.selected', { count })}</span>
        <Button size="sm" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
      <div className="max-w-4xl mx-auto px-3 pb-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1 shrink-0">
          <input
            type="date"
            value={dateValue}
            onChange={e => setDateValue(e.target.value)}
            className="h-9 px-2 text-sm border border-border rounded bg-background text-foreground"
          />
          <Button size="sm" disabled={!dateValue} onClick={() => { onSetDate(dateValue); setDateValue(''); }}>{t('bulk.setDate')}</Button>
        </div>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => onShift('day', 1)}>{t('bulk.plus1Day')}</Button>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => onShift('week', 1)}>{t('bulk.plus1Week')}</Button>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => onShift('month', 1)}>{t('bulk.plus1Month')}</Button>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => onShift('year', 1)}>{t('bulk.plus1Year')}</Button>
        <Button size="sm" variant="outline" className="shrink-0" onClick={onRemoveDate}>{t('bulk.removeDate')}</Button>
      </div>
    </div>
  );
}

const TaskRow = memo(function TaskRow({ task, lists, tags, users, isDone, onComplete, onDelete, onEdit, onRestore, showDate, importanceMode, completingId, showTags, isSelectMode, isSelected, onToggleSelect }) {
  const { t } = useI18n();
  const rowRef = useRef(null);
  const taskListIds = task.list_ids || (task.list_id ? [task.list_id] : []);
  const listColor = lists.find(l => l.id === taskListIds[0])?.color || 'transparent';
  const tagMap = Object.fromEntries(tags.map(tg => [tg.id, tg]));
  const assigneeIds = task.assignees || (task.assigned_to ? [task.assigned_to] : []);
  const assignedUsers = assigneeIds.map(id => users?.find(u => u.id === id)).filter(Boolean);
  const dt = task.occurrence_datetime || task.due_datetime;
  const hasSubs = task.subtasks?.length > 0;
  const doneSubs = hasSubs ? task.subtasks.filter(s => s.completed).length : 0;
  const isCompleting = completingId === task.id;
  const isBgMode = importanceMode === 'background';

  return (
    <div
      ref={rowRef}
      className={`flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors group ${isDone ? 'opacity-50' : ''} ${isCompleting ? 'animate-tumble-down' : ''} ${isBgMode ? `priority-bg-${task.priority}` : ''} ${isSelected ? 'bg-primary/5' : ''}`}
      style={{ borderLeft: `3px solid ${listColor}` }}
      data-testid={`task-row-${task.id}`}
      data-task-id={task.id}
    >
      {isSelectMode ? (
        <button
          className="p-2 -m-2 flex-shrink-0"
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(task.id); }}
        >
          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
            {isSelected && <Check size={10} className="text-primary-foreground" weight="bold" />}
          </span>
        </button>
      ) : (
        <span className="p-2 -m-2 flex-shrink-0 inline-flex" onClick={(e) => { e.stopPropagation(); isDone ? onRestore?.(task) : onComplete?.(task, rowRef.current); }}>
          <Checkbox
            data-testid={`task-check-${task.id}`}
            checked={isDone}
            className="h-5 w-5 pointer-events-none"
          />
        </span>
      )}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={isSelectMode ? () => onToggleSelect?.(task.id) : () => onEdit?.(task)}>
        <div className={`text-sm font-medium truncate ${isDone ? 'line-through text-muted-foreground' : ''}`}>
          {task.title}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {showDate && dt && (
            <span className="text-xs text-muted-foreground flex items-center gap-0.5">
              <CalendarBlank size={11} />{format(parseISO(dt), 'HH:mm') !== '00:00' ? format(parseISO(dt), 'HH:mm') : ''}
            </span>
          )}
          {(task.recurrence_rule || task.is_recurring) && <Badge variant="secondary" className="text-[10px] px-1.5 py-0"><Repeat size={10} className="mr-0.5" />{t('task.repeats')}</Badge>}
          {!isBgMode && task.priority > 0 && <Flag size={12} weight="fill" className={PRIORITY_COLORS[task.priority]} />}
          {hasSubs && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-0.5" data-testid={`subtask-indicator-${task.id}`}>
              <CheckSquare size={12} weight={doneSubs === task.subtasks.length ? 'fill' : 'regular'} className={doneSubs === task.subtasks.length ? 'text-green-500' : ''} />
              {doneSubs}/{task.subtasks.length}
            </span>
          )}
          {showTags && task.tags?.map(tagId => {
            const tag = tagMap[tagId];
            return tag ? <Badge key={tagId} variant="secondary" className="text-[10px] px-1.5 py-0" style={{ borderColor: tag.color, color: tag.color }}>{tag.name}</Badge> : null;
          })}
          {assignedUsers.length > 0 && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-0.5" data-testid={`assigned-user-${task.id}`}>
              {assignedUsers.map(u => (
                <span key={u.id} className="w-3.5 h-3.5 rounded-full inline-flex items-center justify-center text-[7px] font-bold text-white" style={{ backgroundColor: u.avatar_color }} title={u.name}>{u.name?.[0]?.toUpperCase()}</span>
              ))}
              {assignedUsers.length === 1 && assignedUsers[0].name}
            </span>
          )}
        </div>
      </div>
      {!isSelectMode && (isDone ? (
        <Button data-testid={`restore-task-${task.id}`} variant="ghost" size="sm" className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-xs" onClick={() => onRestore?.(task)}>
          <ArrowCounterClockwise size={14} className="mr-1" />
        </Button>
      ) : (
        <button data-testid={`delete-task-${task.id}`} className="p-2 -m-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all" onClick={() => onDelete?.(task)}>
          <Trash size={16} />
        </button>
      ))}
    </div>
  );
});

const DateSection = memo(function DateSection({ label, tasks, lists, tags, users, color, isDoneFn, onComplete, onDelete, onEdit, onRestore, importanceMode, completingId, showTags, isSelectMode, selectedIds, onToggleSelect }) {
  if (tasks.length === 0) return null;
  return (
    <div className="mb-3 animate-slide-up">
      <div className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${color}`}>
        {label}
        <Badge variant="secondary" className="text-[10px] ml-1">{tasks.length}</Badge>
      </div>
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {tasks.map((task, i) => (
          <TaskRow key={`${task.id}-${task.occurrence_datetime || i}`} task={task} lists={lists} tags={tags} users={users} isDone={isDoneFn(task)}
            onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} onRestore={onRestore} showDate importanceMode={importanceMode} completingId={completingId} showTags={showTags}
            isSelectMode={isSelectMode} isSelected={selectedIds?.has(task.id)} onToggleSelect={onToggleSelect} />
        ))}
      </div>
    </div>
  );
});

export default function ListView() {
  const { t, lang } = useI18n();
  // Without this, day headings render as "Mon 3" even with the UI in Korean.
  const dfnsLocale = lang === 'ko' ? koLocale : undefined;
  const { settings } = useSettings();
  // Persisted so they survive navigating away and back (routes are lazy and
  // unmount). `null` on selectedList still means "not initialised yet".
  const [tab, setTab] = usePersistedState('tododuk.list.tab', 'active');
  const [occurrences, setOccurrences] = useState([]);
  const [noDateTasks, setNoDateTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [deletedTasks, setDeletedTasks] = useState([]);
  const [lists, setLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedList, setSelectedList] = usePersistedState('tododuk.list.selected', null);
  const { activeId: workspaceId } = useWorkspace();
  const [users, setUsers] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (selectedList === null && settings.default_list_id) {
      setSelectedList(settings.default_list_id);
    } else if (selectedList === null) {
      setSelectedList('all');
    }
  }, [settings.default_list_id, selectedList, setSelectedList]);

  const prevWsRef = useRef(workspaceId);
  useEffect(() => {
    if (prevWsRef.current !== workspaceId) {
      prevWsRef.current = workspaceId;
      setSelectedList('all');
    }
  }, [workspaceId, setSelectedList]);

  // A persisted selection can outlive the list itself — deleted, or belonging
  // to a workspace we are no longer in. Fall back instead of rendering empty.
  useEffect(() => {
    if (selectedList && selectedList !== 'all' && lists.length
        && !lists.some((l) => l.id === selectedList)) {
      setSelectedList('all');
    }
  }, [lists, selectedList, setSelectedList]);
  const [editingTask, setEditingTask] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [recurringDelete, setRecurringDelete] = useState(null);
  const [completingId, setCompletingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (selectedList === null) return;
    try {
      const now = new Date();
      const start = subDays(now, 7);
      const end = addDays(now, 90);
      const ws = workspaceId ? { workspace_id: workspaceId } : {};
      const requests = [
        api.get('/tasks/occurrences', { params: { start: start.toISOString(), end: end.toISOString(), ...ws } }),
        api.get('/tasks', { params: { ...(selectedList !== 'all' ? { list_id: selectedList } : {}), ...ws } }),
        api.get('/lists', { params: ws }), api.get('/tags', { params: ws }), api.get('/users'),
      ];
      if (settings.show_deleted_bin) requests.push(api.get('/tasks/deleted'));
      const results = await Promise.all(requests);
      const [occRes, allRes, listsRes, tagsRes, usersRes] = results;
      const checkDone = (tsk) => tsk.status === 'done' || !!tsk.completed_at;
      let occs = occRes.data;
      if (selectedList !== 'all') occs = occs.filter(o => (o.list_ids || []).includes(selectedList));
      setOccurrences(occs.filter(o => !checkDone(o)));
      const all = allRes.data;
      setNoDateTasks(all.filter(tsk => !tsk.due_datetime && !checkDone(tsk)));
      setCompletedTasks(all.filter(tsk => checkDone(tsk)));
      setLists(listsRes.data); setTags(tagsRes.data); setUsers(usersRes.data);
      if (settings.show_deleted_bin && results[5]) setDeletedTasks(results[5].data);
      else setDeletedTasks([]);
    } catch { toast.error(t('common.error')); }
  }, [selectedList, t, settings.show_deleted_bin, workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isDone = (task) => task.status === 'done' || !!task.completed_at;

  // Optimistic helpers: mutate local pools immediately, refetch only on error
  const removeFromActive = useCallback((task) => {
    if (task.is_recurring && task.occurrence_datetime) {
      setOccurrences(prev => prev.filter(o => !(o.id === task.id && o.occurrence_datetime === task.occurrence_datetime)));
    } else {
      setOccurrences(prev => prev.filter(o => o.id !== task.id));
      setNoDateTasks(prev => prev.filter(tk => tk.id !== task.id));
    }
  }, []);

  const grouped = useMemo(() => {
    const today = startOfDay(new Date());
    const tmrw = addDays(today, 1);
    const groups = { overdue: [], today: [], tomorrow: [] };

    const daySlots = [];
    if (settings.show_next_7_days) {
      for (let i = 2; i < 7; i++) {
        const d = addDays(today, i);
        const key = format(d, 'yyyy-MM-dd');
        daySlots.push({ date: d, key, label: format(d, 'EEE d', { locale: dfnsLocale }), tasks: [] });
      }
    }

    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const monthEnd = endOfMonth(today);
    const thisWeekTasks = [];
    const thisMonthTasks = [];
    const laterTasks = [];
    const cutoff = settings.show_next_7_days ? addDays(today, 7) : addDays(today, 2);

    for (const occ of occurrences) {
      const dtStr = occ.occurrence_datetime || occ.due_datetime;
      if (!dtStr) continue;
      try {
        const dt = startOfDay(parseISO(dtStr));
        if (isBefore(dt, today)) { groups.overdue.push(occ); continue; }
        if (isSameDay(dt, today)) { groups.today.push(occ); continue; }
        if (isSameDay(dt, tmrw)) { groups.tomorrow.push(occ); continue; }

        let placed = false;
        if (settings.show_next_7_days) {
          for (const slot of daySlots) {
            if (isSameDay(dt, slot.date)) { slot.tasks.push(occ); placed = true; break; }
          }
        }
        if (placed) continue;

        if (settings.show_this_week && !isAfter(dt, weekEnd) && isAfter(dt, cutoff)) {
          thisWeekTasks.push(occ); continue;
        }
        if (settings.show_this_month && !isAfter(dt, monthEnd)) {
          thisMonthTasks.push(occ); continue;
        }
        laterTasks.push(occ);
      } catch { laterTasks.push(occ); }
    }

    return { ...groups, daySlots, thisWeekTasks, thisMonthTasks, laterTasks };
  }, [occurrences, settings, dfnsLocale]);

  const completeOnServer = async (task) => {
    const body = task.is_recurring && task.occurrence_datetime ? { occurrence_datetime: task.occurrence_datetime } : {};
    // optimistic: drop from active pools now, surface in completed unless it's one occurrence
    removeFromActive(task);
    if (!(task.is_recurring && task.occurrence_datetime)) {
      setCompletedTasks(prev => [{ ...task, status: 'done', completed_at: new Date().toISOString() }, ...prev.filter(tk => tk.id !== task.id)]);
    }
    try {
      await api.post(`/tasks/${task.id}/complete`, body);
    } catch {
      toast.error(t('common.error'));
      fetchData(); // rollback by resync
    }
  };

  const handleComplete = (task, element) => {
    // Sound and confetti are separate toggles; either can be on alone.
    if (settings.completion_sound) playCompletionSound();
    if (settings.completion_animation) {
      setCompletingId(task.id);
      playCompletionAnimation(element);
      setTimeout(() => { completeOnServer(task); setCompletingId(null); }, 750);
    } else {
      completeOnServer(task);
    }
  };

  const handleRestore = async (task) => {
    setCompletedTasks(prev => prev.filter(tk => tk.id !== task.id));
    const restored = { ...task, status: 'todo', completed_at: null };
    if (restored.due_datetime) setOccurrences(prev => [...prev, { ...restored, occurrence_datetime: restored.due_datetime }]);
    else setNoDateTasks(prev => [...prev, restored]);
    try {
      await api.post(`/tasks/${task.id}/uncomplete`);
    } catch { toast.error(t('common.error')); fetchData(); }
  };

  const softDelete = async (task) => {
    setOccurrences(prev => prev.filter(o => o.id !== task.id));
    setNoDateTasks(prev => prev.filter(tk => tk.id !== task.id));
    setCompletedTasks(prev => prev.filter(tk => tk.id !== task.id));
    try {
      await api.delete(`/tasks/${task.id}`);
      toast(t('task.movedToTrash'), {
        action: {
          label: t('task.undo'),
          onClick: async () => {
            try { await api.put(`/tasks/${task.id}`, { deleted_at: null }); fetchData(); }
            catch { toast.error(t('common.error')); }
          },
        },
      });
    } catch { toast.error(t('common.error')); fetchData(); }
  };

  const handleDelete = (task) => {
    if (task.recurrence_rule || task.is_recurring) { setRecurringDelete(task); return; }
    softDelete(task);
  };

  const handleSkipOccurrence = async () => {
    const task = recurringDelete;
    setRecurringDelete(null);
    const dt = task.occurrence_datetime || task.due_datetime;
    removeFromActive(task);
    try { await api.post(`/tasks/${task.id}/occurrences/${encodeURIComponent(dt)}/skip`); }
    catch { toast.error(t('common.error')); fetchData(); }
  };

  const handleDeleteSeries = () => {
    const task = recurringDelete;
    setRecurringDelete(null);
    softDelete(task);
  };

  const handleHardDelete = async (taskId) => {
    setDeletedTasks(prev => prev.filter(tk => tk.id !== taskId));
    try { await api.delete(`/tasks/${taskId}/hard-delete`); }
    catch { toast.error(t('common.error')); fetchData(); }
  };

  const handleRestoreDeleted = async (task) => {
    setDeletedTasks(prev => prev.filter(tk => tk.id !== task.id));
    try {
      await api.put(`/tasks/${task.id}`, { deleted_at: null });
      fetchData();
    } catch { toast.error(t('common.error')); fetchData(); }
  };

  const openEditor = (task) => {
    setEditingTask(task);
    setEditorOpen(true);
  };

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
    exitSelectMode();
  }, [exitSelectMode, setTab]);

  const handleBulkSetDate = useCallback(async (dateStr) => {
    try {
      const dt = new Date(dateStr + 'T12:00:00').toISOString();
      await api.put('/tasks/bulk-update', { task_ids: Array.from(selectedIds), updates: { due_datetime: dt } });
      exitSelectMode();
      fetchData();
    } catch { toast.error(t('common.error')); }
  }, [selectedIds, exitSelectMode, fetchData, t]);

  const handleBulkShift = useCallback(async (unit, amount) => {
    const pool = [...occurrences, ...noDateTasks];
    const fns = { day: addDays, week: addWeeks, month: addMonths, year: addYears };
    try {
      const calls = Array.from(selectedIds).map(id => {
        const task = pool.find(tk => tk.id === id);
        if (!task || task.is_recurring || task.recurrence_rule || task.is_occurrence) return null;
        const dtStr = task.occurrence_datetime || task.due_datetime;
        if (!dtStr) return null;
        return api.put(`/tasks/${id}`, { due_datetime: fns[unit](parseISO(dtStr), amount).toISOString() });
      }).filter(Boolean);
      if (calls.length) await Promise.all(calls);
      exitSelectMode();
      fetchData();
    } catch { toast.error(t('common.error')); }
  }, [selectedIds, occurrences, noDateTasks, exitSelectMode, fetchData, t]);

  const handleBulkRemoveDate = useCallback(async () => {
    try {
      await api.put('/tasks/bulk-update', { task_ids: Array.from(selectedIds), updates: { due_datetime: null } });
      exitSelectMode();
      fetchData();
    } catch { toast.error(t('common.error')); }
  }, [selectedIds, exitSelectMode, fetchData, t]);

  const overdueCount = grouped.overdue.length;
  // Count tasks, not occurrences: a weekly chore expanded over the 90-day
  // window is still one task. Overdue occurrences live in their own tab.
  const totalActive = useMemo(() => {
    const overdue = new Set(settings.show_incomplete_bin ? grouped.overdue : []);
    const ids = new Set(noDateTasks.map(tk => tk.id));
    for (const o of occurrences) if (!overdue.has(o)) ids.add(o.id);
    return ids.size;
  }, [occurrences, noDateTasks, grouped.overdue, settings.show_incomplete_bin]);
  const defaultListId = settings.default_list_id || lists[0]?.id;
  const sectionProps = useMemo(() => ({
    lists, tags, users, isDoneFn: isDone, onComplete: handleComplete, onDelete: handleDelete,
    onEdit: openEditor, onRestore: handleRestore, importanceMode: settings.importance_display,
    completingId, showTags: settings.show_tags,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lists, tags, users, settings.importance_display, completingId, settings.show_tags]);
  const selectProps = { isSelectMode, selectedIds, onToggleSelect: toggleSelect };
  const currentList = selectedList !== 'all' ? lists.find(l => l.id === selectedList) : null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl pb-20" data-testid="list-view">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="list-view-title">{t('nav.list')}</h1>
        <div className="flex items-center gap-2">
          <Select value={selectedList} onValueChange={setSelectedList}>
            <SelectTrigger hideChevron className={`w-9 px-0 justify-center shrink-0 ${selectedList !== 'all' ? 'text-primary border-primary' : ''}`} data-testid="list-filter" aria-label={t('common.all')}>
              <Funnel size={16} weight={selectedList !== 'all' ? 'fill' : 'regular'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {lists.map(l => (
                <SelectItem key={l.id} value={l.id}>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: l.color }} />{l.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentList && users.length > 1 && (
            <Button data-testid="share-list-btn" size="sm" variant="outline" onClick={() => setShareDialogOpen(true)}>
              <ShareNetwork size={16} className="mr-1" /> {t('settings.share')}
            </Button>
          )}
          {(tab === 'active' || tab === 'overdue') && !isSelectMode && (
            <Button size="sm" variant="outline" onClick={() => setIsSelectMode(true)}>{t('task.select')}</Button>
          )}
          {isSelectMode && (
            <Button size="sm" variant="ghost" onClick={exitSelectMode}>{t('common.cancel')}</Button>
          )}
          {!isSelectMode && (
            <Button data-testid="add-task-btn" size="sm" onClick={() => { setEditingTask(null); setEditorOpen(true); }}>
              <Plus size={16} className="mr-1" /> {t('task.add')}
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="max-w-full overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="active" data-testid="tab-active">
              {t('task.active')} {totalActive > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{totalActive}</Badge>}
            </TabsTrigger>
            {settings.show_incomplete_bin && (
              <TabsTrigger value="overdue" data-testid="tab-overdue">
                {t('task.overdue')} {overdueCount > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{overdueCount}</Badge>}
              </TabsTrigger>
            )}
            <TabsTrigger value="completed" data-testid="tab-completed">
              {t('task.completedTasks')} {completedTasks.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{completedTasks.length}</Badge>}
            </TabsTrigger>
            {settings.show_deleted_bin && (
              <TabsTrigger value="deleted" data-testid="tab-deleted">
                {t('task.deleted')} {deletedTasks.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{deletedTasks.length}</Badge>}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      {tab === 'active' && (
        <>
          <div className="mb-4">
            <QuickAdd onTaskAdded={fetchData} listId={selectedList !== 'all' ? selectedList : defaultListId} tags={tags} />
          </div>
          {totalActive === 0 ? (
            <div className="text-center py-16 animate-fade-in" data-testid="empty-list">
              <ClipboardText size={64} className="mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-lg font-heading text-muted-foreground">{t('task.noTasks')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('task.addFirst')}</p>
            </div>
          ) : (
            <div data-testid="task-sections">
              {!settings.show_incomplete_bin && <DateSection label={t('task.overdue')} tasks={grouped.overdue} color="text-red-500" {...sectionProps} {...selectProps} />}
              <DateSection label={t('task.today')} tasks={grouped.today} color="text-primary" {...sectionProps} {...selectProps} />
              <DateSection label={t('task.tomorrow')} tasks={grouped.tomorrow} color="text-foreground" {...sectionProps} {...selectProps} />
              {settings.show_next_7_days && grouped.daySlots?.map(slot => (
                <DateSection key={slot.key} label={slot.label} tasks={slot.tasks} color="text-foreground" {...sectionProps} {...selectProps} />
              ))}
              {settings.show_this_week && <DateSection label={t('task.thisWeek')} tasks={grouped.thisWeekTasks} color="text-foreground" {...sectionProps} {...selectProps} />}
              {settings.show_this_month && <DateSection label={t('task.thisMonth')} tasks={grouped.thisMonthTasks} color="text-muted-foreground" {...sectionProps} {...selectProps} />}
              {settings.show_later && <DateSection label={t('task.later')} tasks={grouped.laterTasks} color="text-muted-foreground" {...sectionProps} {...selectProps} />}
              {noDateTasks.length > 0 && <DateSection label={t('task.noDate')} tasks={noDateTasks} color="text-muted-foreground" {...sectionProps} {...selectProps} />}
            </div>
          )}
        </>
      )}

      {tab === 'overdue' && settings.show_incomplete_bin && (
        <>
          {overdueCount === 0 ? (
            <div className="text-center py-16 animate-fade-in" data-testid="empty-overdue">
              <CheckSquare size={64} className="mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-lg font-heading text-muted-foreground">{t('task.noOverdue')}</p>
            </div>
          ) : (
            <div data-testid="overdue-sections">
              <DateSection label={t('task.overdue')} tasks={grouped.overdue} color="text-red-500" {...sectionProps} {...selectProps} />
            </div>
          )}
        </>
      )}

      {tab === 'completed' && (
        <>
          {completedTasks.length === 0 ? (
            <div className="text-center py-16 animate-fade-in" data-testid="empty-completed">
              <CheckSquare size={64} className="mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-lg font-heading text-muted-foreground">{t('task.completedTasks')}</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="completed-list">
              {completedTasks.map(task => (
                <TaskRow key={task.id} task={task} lists={lists} tags={tags} users={users} isDone onRestore={handleRestore} onDelete={handleDelete} onEdit={openEditor} showDate={!!task.due_datetime} importanceMode={settings.importance_display} completingId={null} showTags={settings.show_tags} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'deleted' && settings.show_deleted_bin && (
        <>
          {deletedTasks.length === 0 ? (
            <div className="text-center py-16 animate-fade-in" data-testid="empty-deleted">
              <Trash size={64} className="mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-lg font-heading text-muted-foreground">{t('task.noDeleted')}</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="deleted-list">
              {deletedTasks.map(task => (
                <div key={task.id} className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/40 group opacity-60">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate line-through text-muted-foreground">{task.title}</div>
                    {task.deleted_at && <div className="text-xs text-muted-foreground">{t('task.deleted')} {format(parseISO(task.deleted_at), 'MMM d', { locale: dfnsLocale })}</div>}
                  </div>
                  <Button size="sm" variant="ghost" className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-xs" onClick={() => handleRestoreDeleted(task)}>
                    <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                  </Button>
                  <button className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all" onClick={() => handleHardDelete(task.id)}>
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <TaskEditor open={editorOpen} onClose={() => { setEditorOpen(false); setEditingTask(null); }} task={editingTask} lists={lists} tags={tags} users={users} onSaved={fetchData} />

      <AlertDialog open={!!recurringDelete} onOpenChange={(open) => { if (!open) setRecurringDelete(null); }}>
        <AlertDialogContent data-testid="recurring-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('task.deleteRecurringTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('task.deleteRecurringDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recurring-delete-cancel">{t('common.cancel')}</AlertDialogCancel>
            {(recurringDelete?.occurrence_datetime || recurringDelete?.due_datetime) && (
              <AlertDialogAction data-testid="recurring-delete-occurrence" onClick={handleSkipOccurrence}>
                {t('task.deleteOccurrence')}
              </AlertDialogAction>
            )}
            <AlertDialogAction data-testid="recurring-delete-series" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDeleteSeries}>
              {t('task.deleteSeries')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareListDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        list={currentList}
        users={users}
        onShared={fetchData}
      />

      {isSelectMode && selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          onSetDate={handleBulkSetDate}
          onShift={handleBulkShift}
          onRemoveDate={handleBulkRemoveDate}
          onCancel={exitSelectMode}
        />
      )}
    </div>
  );
}
